import type { Command } from "commander";
import { CostCalculator } from "../../cost/CostCalculator.js";
import { AiModelsCatalogClient } from "../../catalog/AiModelsCatalogClient.js";
import { resolveCatalogCacheTtlMs } from "../../cache/modelCache.js";

function formatUsd(n: number): string {
  return `$${n.toFixed(6)}`;
}

export function registerCostCommand(program: Command): void {
  program
    .command("cost")
    .description("Estimate cost from catalog pricing")
    .requiredOption("--model <id>", "Model id or profile/choice key")
    .requiredOption("--prompt-tokens <n>", "Prompt token count", (v) => Number.parseInt(v, 10))
    .requiredOption(
      "--completion-tokens <n>",
      "Completion token count",
      (v) => Number.parseInt(v, 10),
    )
    .option("--cached-tokens <n>", "Cached token count", (v) => Number.parseInt(v, 10))
    .option("--reasoning-tokens <n>", "Reasoning token count", (v) => Number.parseInt(v, 10))
    .option("--provider <id>", "Provider for pricing selection", "openrouter")
    .option("--json", "Output raw AiCostResult JSON")
    .option("--bundled-only", "Use src/data catalogs only (no HTTP)")
    .action(async (opts: {
      model: string;
      promptTokens: number;
      completionTokens: number;
      cachedTokens?: number;
      reasoningTokens?: number;
      provider: string;
      json?: boolean;
      bundledOnly?: boolean;
    }) => {
      const catalogClient = new AiModelsCatalogClient({
        cacheTtlMs: resolveCatalogCacheTtlMs(),
        bundledOnly: opts.bundledOnly,
      });

      const calculator = new CostCalculator(catalogClient);

      const result = await calculator.calculate({
        tokens: {
          prompt: opts.promptTokens,
          completion: opts.completionTokens,
          total: opts.promptTokens + opts.completionTokens,
          cached: opts.cachedTokens,
          reasoning: opts.reasoningTokens,
        },
        provider: opts.provider,
        modelUsed: opts.model,
      });

      if (opts.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }

      const b = result.breakdown;
      console.log(`Model:        ${result.resolvedModelId}`);
      if (result.usedModel && result.usedModel !== result.resolvedModelId) {
        console.log(`Used model:   ${result.usedModel}`);
      }
      console.log(
        `Prompt:       ${opts.promptTokens.toLocaleString()} tokens  →  ${formatUsd(b?.promptCostUsd ?? 0)}`,
      );
      console.log(
        `Completion:   ${opts.completionTokens.toLocaleString()} tokens  →  ${formatUsd(b?.completionCostUsd ?? 0)}`,
      );
      console.log(
        `Request fee:              →  ${formatUsd(b?.requestFlatCostUsd ?? 0)}`,
      );
      console.log("─".repeat(38));
      console.log(`Total:                    →  ${formatUsd(result.cost)}`);
      console.log(
        `Via OpenRouter: ${result.routedViaOpenRouter ? "yes" : "no"}`,
      );
    });
}
